import { useEffect } from 'react';

import isEqual from 'lodash-es/isEqual';
import { LinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Routes,
  Route,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
  useMatch,
} from 'react-router';
import { useTitle } from 'react-use';

import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ReadOnlyBanner } from '@/components/atoms/ReadOnlyBanner/ReadOnlyBanner';
import {
  PageSubHeader,
  PageSubHeaderActions,
} from '@/components/organisms/PageSubHeader/PageSubHeader';
import { ResponsiveSidebarPanel } from '@/components/organisms/Sidebar/ResponsiveSidebarPanel';
import { SidebarLayout } from '@/components/organisms/Sidebar/SidebarLayout';
import Layout from '@/components/templates/Layout';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import PermissionsWrapper from '@/components/templates/PermissionsWrapper';
import { useAccount } from '@/hooks/useAccount';
import useMembers from '@/hooks/useMembers';
import { usePermissions } from '@/hooks/usePermissions';
import AnalyticsNavigation from '@/modules/analytics/components/AnalyticsNavigation/AnalyticsNavigation';
import { selectFiltersByType } from '@/modules/analytics/redux/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { AnalyticsType } from '@/shared/types/analytics';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { getRoles } from '@/util/permissions';

import { MetricEditorPanel } from './components/CustomReports/MetricEditorPanel';
import {
  ReportBreadcrumb,
  ReportDetailBreadcrumb,
} from './components/CustomReports/ReportDetailBreadcrumb';
import { ReportDetailHeaderAction } from './components/CustomReports/ReportDetailHeaderAction';
import { DefaultAnalyticsBreadcrumb } from './components/DefaultAnalyticsBreadcrumb';
import DisabledAnalytics from './components/DisabledAnalytics';
import { MY_PERFORMANCE_PATH, USER_FEEDBACK_PATH } from './constants';
import useAnalyticsHelper from './hooks/useAnalyticsHelper';
import { ComparisonModeProvider } from './hooks/useComparisonMode';
import { ReportDetailUiProvider, useReportDetailUi } from './hooks/useReportDetailUi';
import { AccountChatLogs } from './pages/AccountChatLogs';
import { AiAgents } from './pages/AiAgents';
import { CustomReport } from './pages/CustomReports/CustomReport';
import { HumanAgents } from './pages/HumanAgents';
import { MyPerformance } from './pages/MyPerformance';
import { OverviewReport } from './pages/OverviewReport';
import { UserFeedback } from './pages/UserFeedback';
import { resetFilters } from './redux/actions';

/** Redirects to a new path while preserving query params (for backwards compatibility) */
const RedirectWithParams = ({ to }: { to: string }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
};

const MetricPanel = ({ reportId }: { reportId: string }) => {
  const { t } = useTranslation();
  const { editor, closeEditor } = useReportDetailUi(reportId);

  if (!editor) {
    return null;
  }
  return (
    <ResponsiveSidebarPanel
      title={
        editor.kind === 'edit'
          ? t('analytics.reports_detail_edit_metric')
          : t('analytics.reports_detail_add_metric')
      }
      onClose={closeEditor}
    >
      <MetricEditorPanel reportId={reportId} />
    </ResponsiveSidebarPanel>
  );
};

const AnalyticsRouter: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const slug = useSelector(selectAccountSlug);
  const { slug: urlSlug } = useParams();
  const { account, accountRole } = useAccount();
  const { member, listStatus: membersStatus } = useMembers();
  useAnalyticsHelper();
  const { listStatus } = usePermissions();
  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'analytics', actions.READ)
  );
  const canWriteFilters = useSelector((state: RootState) =>
    getPermissions(state, 'filters', actions.WRITE)
  );
  const canWriteReports = useSelector((state: RootState) =>
    getPermissions(state, 'reports', actions.WRITE)
  );

  const isAnalyticsDisabled = account?.opt_out;

  const urlFilters = useSelector(
    (state: RootState) => selectFiltersByType(state, AnalyticsType.ACCOUNT),
    isEqual
  );

  useTitle(t('pages.analytics_simple'));

  const reportDetailMatch = useMatch('/:slug/analytics/reports/:reportId');
  const userFeedbackMatch = useMatch('/:slug/analytics/user-feedback');

  useEffect(() => {
    if (slug !== urlSlug && urlFilters.length > 0) {
      dispatch(resetFilters());
      navigate({ ...window.location, search: '' }, { replace: true });
    }
  }, [dispatch, urlFilters.length, navigate, slug, urlSlug]);

  const noAnalyticsAccess = listStatus === 'success' && !canRead;
  const isChatAgent = getRoles(member?.role_ids ?? [], accountRole).isChatAgent;

  // Chat agents without analytics access get a single "My performance" page.
  // Wait for permissions and roles, so they never mount the account-wide pages.
  const isDecidingAccess =
    listStatus === 'pending' || (noAnalyticsAccess && membersStatus === 'pending');
  if (!isAnalyticsDisabled && isDecidingAccess) {
    return <Layout>{null}</Layout>;
  }

  if (!isAnalyticsDisabled && noAnalyticsAccess && isChatAgent) {
    return (
      <Layout>
        <ComparisonModeProvider>
          <PageSubHeader breadcrumbs={<ReportBreadcrumb name={t('analytics.my_performance')} />} />
          <PageContentWrapper className="pt-0 pl-4">
            <Routes>
              <Route path={MY_PERFORMANCE_PATH} element={<MyPerformance />} />
              <Route
                path="*"
                element={<RedirectWithParams to={`/${slug}/analytics/${MY_PERFORMANCE_PATH}`} />}
              />
            </Routes>
          </PageContentWrapper>
        </ComparisonModeProvider>
      </Layout>
    );
  }

  if (isAnalyticsDisabled || noAnalyticsAccess) {
    return (
      <Layout>
        <DisabledAnalytics />
      </Layout>
    );
  }

  const reportDetailId = reportDetailMatch?.params.reportId;

  let breadcrumbs = <DefaultAnalyticsBreadcrumb />;
  if (reportDetailId) {
    breadcrumbs = <ReportDetailBreadcrumb reportId={reportDetailId} />;
  } else if (userFeedbackMatch) {
    breadcrumbs = <ReportBreadcrumb name={t('analytics.user_feedback')} />;
  }

  return (
    <Layout>
      <PermissionsWrapper module="analytics">
        <ComparisonModeProvider>
          <ReportDetailUiProvider>
            <SidebarLayout
              autoSaveId="analytics-report-sidebar"
              sidebar={
                reportDetailId && canWriteReports && <MetricPanel reportId={reportDetailId} />
              }
            >
              <PageSubHeader
                breadcrumbs={breadcrumbs}
                actions={
                  <PageSubHeaderActions>
                    {reportDetailId && canWriteReports && (
                      <ReportDetailHeaderAction reportId={reportDetailId} />
                    )}
                    {!reportDetailId && (
                      <InfoTooltip content={t('analytics.button_share')} side="bottom" showArrow>
                        <div>
                          <CopyButton
                            data={window.location.href}
                            iconPosition="center"
                            icon={<LinkIcon size={16} color="var(--muted-foreground)" />}
                          />
                        </div>
                      </InfoTooltip>
                    )}
                  </PageSubHeaderActions>
                }
              />
              {!(reportDetailId ? canWriteReports : canWriteFilters) && <ReadOnlyBanner />}
              <div className="flex h-full min-h-0">
                <AnalyticsNavigation />
                <div className="min-w-0 flex-1 overflow-hidden">
                  <PageContentWrapper
                    noGutters={location.pathname.includes('account-chat-logs')}
                    className="pt-0 pl-4"
                  >
                    <Routes>
                      <Route path="/" element={<Navigate to="overview" replace />} />
                      <Route path="/overview" element={<OverviewReport />} />
                      <Route path="/ai-agents" element={<AiAgents />} />
                      <Route path="/human-agents" element={<HumanAgents />} />
                      <Route path="/account-chat-logs" element={<AccountChatLogs />} />

                      <Route path="/reports" element={<Navigate to="../overview" replace />} />
                      <Route
                        path="/reports/:reportId"
                        element={
                          <PermissionsWrapper module="reports">
                            <CustomReport />
                          </PermissionsWrapper>
                        }
                      />
                      <Route
                        path={USER_FEEDBACK_PATH}
                        element={
                          <PermissionsWrapper module="reports">
                            <UserFeedback />
                          </PermissionsWrapper>
                        }
                      />
                      {/* Redirects from removed pages to their replacements */}
                      <Route path="/usage" element={<RedirectWithParams to="../ai-agents" />} />
                      <Route
                        path="/ai-agents-effectiveness"
                        element={<RedirectWithParams to="../ai-agents" />}
                      />
                      <Route
                        path="/human-agents-effectiveness"
                        element={<RedirectWithParams to="../human-agents" />}
                      />
                      <Route
                        path="/agents-performance"
                        element={<RedirectWithParams to="../ai-agents" />}
                      />
                      <Route
                        path="/customer-satisfaction"
                        element={<RedirectWithParams to="../ai-agents" />}
                      />
                      {/* Redirect from old route name for backwards compatibility */}
                      <Route
                        path="/human-chat-logs"
                        element={<RedirectWithParams to="../account-chat-logs" />}
                      />
                    </Routes>
                  </PageContentWrapper>
                </div>
              </div>
            </SidebarLayout>
          </ReportDetailUiProvider>
        </ComparisonModeProvider>
      </PermissionsWrapper>
    </Layout>
  );
};

export default AnalyticsRouter;
